import type { DateValue } from "@internationalized/date";
import {
  Announcement03,
  Clipboard,
  Globe05,
  Palette,
  ShieldZap,
  ShoppingBag01,
  TrendUp01,
  Users01,
} from "@untitledui/icons";
import type { PropsWithChildren } from "react";
import { useCreatorFilters } from "@/api/creator-filters";
import { type CreatorFilterRanges, useCreatorFilterRanges } from "@/api/filter-ranges";
import { useAdvancedFilters } from "@/api/tiktok-shop";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Label } from "@/components/base/input/label";
import { HorizontalTabs } from "@/components/base/tabs/horizontal-tabs";
import {
  ContentFocusFilter,
  ProductionQualityFilter,
  ToneAndApproachFilter,
  TopSellingCategoriesFilter,
} from "@/components/filters/creator-filters";
import {
  CampaignFilter,
  CohortFilter,
  CreatorListFilter,
  ProductFilter,
  RiskTypeFilter,
  TagsFilterSection,
} from "@/components/filters/extra-filters";
import { PercentileSelector, type PercentileValue } from "@/components/filters/percentile-selector";
import { PillField } from "@/components/filters/pill-field";
import {
  FollowersRangeFilter,
  GmvRangeFilter,
  PerformanceSimpleRange,
  VideosAtRiskPercentFilter,
} from "@/components/filters/range-filters";
import {
  CREATOR_UNAVAILABLE_REASONS,
  emptyRangeReason,
  FilterPanelContainer,
  FilterPanelHeader,
  FilterSection,
  LabelWithTooltip,
  tabUnavailability,
  UnavailableRange,
  unavailableUnless,
  useUnavailableReasons,
  zeroMaxReason,
} from "@/components/filters/shared";
import { Accordion } from "@/components/ui/accordion";
import { Form, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useIsAssurance } from "@/hooks/use-is-assurance";
import { useFilters } from "@/providers/filter-provider";
import { RISK_TIER_LABELS } from "@/providers/filter-registry";

type CreatorFilterPanelProps = {
  open: boolean;
  close: () => void;
  clearAll?: () => void;
  dateRange?: { start?: DateValue; end?: DateValue } | null;
} & PropsWithChildren;

export const CreatorFilterPanel = ({ open, close, clearAll, dateRange }: CreatorFilterPanelProps) => {
  const { reset } = useFilters();
  const { data: filterRanges, isLoading } = useCreatorFilterRanges({
    range: dateRange ?? null,
    enabled: open,
  });

  const handleClear = () => {
    clearAll?.();
    reset();
  };

  return (
    <FilterPanelContainer open={open}>
      <FilterPanelHeader onClear={handleClear} onClose={close} />
      <div className="flex-1 overflow-y-auto pt-4 px-6 pb-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingIndicator size="md" label="Loading filters..." />
          </div>
        ) : (
          <CreatorFilters filterRanges={filterRanges} />
        )}
      </div>
    </FilterPanelContainer>
  );
};

// =============================================================================
// MAIN CREATOR FILTERS COMPONENT
// =============================================================================

// Helper to convert string array to options format
const toOptions = (items?: string[]) => items?.map((item) => ({ id: item, label: item }));

// Helper to convert string array to title-cased options (replaces underscores with spaces)
const toTitleCase = (str: string) =>
  str
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const toTitleCaseOptions = (items?: string[]) => items?.map((item) => ({ id: item, label: toTitleCase(item) }));

type CreatorFiltersProps = {
  filterRanges?: CreatorFilterRanges;
};

const BRAND_INACTIVE_TIP = {
  title: "No merchant accounts connected",
  description: "Add them in Settings → Merchant details → Merchant accounts to use this filter",
} as const;

const CREATOR_OPTION_REASONS = {
  riskTier: CREATOR_UNAVAILABLE_REASONS.riskTier,
  riskId: CREATOR_UNAVAILABLE_REASONS.riskType,
  creatorLevel: CREATOR_UNAVAILABLE_REASONS.creatorLevel,
  categoryPro: CREATOR_UNAVAILABLE_REASONS.categoryPro,
  creatorLanguage: CREATOR_UNAVAILABLE_REASONS.creatorLanguage,
  maleAudiencePercent: CREATOR_UNAVAILABLE_REASONS.audienceShare,
  femaleAudiencePercent: CREATOR_UNAVAILABLE_REASONS.audienceShare,
  age: CREATOR_UNAVAILABLE_REASONS.age,
};

const CreatorFilters = ({ filterRanges }: CreatorFiltersProps) => {
  const { form, isActive } = useFilters();
  const isAssurance = useIsAssurance();
  const { data: creatorFiltersMeta, isLoading: isCreatorFiltersLoading } = useCreatorFilters();
  const brandDisabled = !isCreatorFiltersLoading && !creatorFiltersMeta?.hasBrandCreators;
  // Advanced marketplace filters (creator level, category pro, language) are a per-shop TikTok
  // capability, shared with the Candidate Explorer via the same endpoint (see UNB-1790).
  const { data: advancedFilters } = useAdvancedFilters();
  const availability = filterRanges?.availability;
  const unavailable = useUnavailableReasons(availability, CREATOR_OPTION_REASONS);

  return (
    <Form {...form}>
      <div className="flex flex-col gap-6">
        {/* GMV - Top Level - Only show when real data exists */}
        {isActive("minGmv") && isActive("maxGmv") && filterRanges?.gmv && (
          <UnavailableRange
            form={form}
            fields={["minGmv", "maxGmv"]}
            reason={emptyRangeReason(filterRanges.gmv, CREATOR_UNAVAILABLE_REASONS.gmv)}
          >
            <GmvRangeFilter
              form={form}
              rangeData={filterRanges.gmv}
              max={filterRanges.gmv.max || undefined}
              showHistogram
            />
          </UnavailableRange>
        )}

        {/* Collaboration Type - Top Level */}
        {isActive("collaborationType") && (
          <FormField
            control={form.control}
            name="collaborationType"
            render={({ field }) => (
              <div className="flex flex-col gap-2">
                <Label className="text-sm text-tertiary">Collaboration Type</Label>
                <HorizontalTabs
                  tabs={[
                    { id: "all", label: "All" },
                    {
                      id: "open",
                      label: "Open",
                      ...tabUnavailability(
                        unavailableUnless(
                          availability?.collaborationType,
                          CREATOR_UNAVAILABLE_REASONS.openCollaboration,
                        )?.("open"),
                        field.value === "open",
                      ),
                    },
                    {
                      id: "target",
                      label: "Target",
                      ...tabUnavailability(
                        unavailableUnless(
                          availability?.collaborationType,
                          CREATOR_UNAVAILABLE_REASONS.targetCollaboration,
                        )?.("target"),
                        field.value === "target",
                      ),
                    },
                  ]}
                  activeTab={field.value ?? "all"}
                  onTabChange={(tabId) => field.onChange(tabId === "all" ? undefined : tabId)}
                />
              </div>
            )}
          />
        )}

        <Accordion type="single" collapsible className="w-full">
          {/* Performance Section */}
          {isActive("minCtr") && (
            <FilterSection id="performance" icon={<TrendUp01 className="w-5 h-5" />} title="Performance">
              <UnavailableRange
                form={form}
                fields={["minCtr", "maxCtr"]}
                reason={emptyRangeReason(filterRanges?.ctr, CREATOR_UNAVAILABLE_REASONS.ctr)}
              >
                <PerformanceSimpleRange
                  form={form}
                  label="CTR"
                  tooltip="Click-Through Rate"
                  description="Percentage of views that result in clicks."
                  minField="minCtr"
                  maxField="maxCtr"
                  min={filterRanges?.ctr?.min ?? 0}
                  max={filterRanges?.ctr?.max || 100}
                  format="percent"
                />
              </UnavailableRange>
              <UnavailableRange
                form={form}
                fields={["minCtor", "maxCtor"]}
                reason={emptyRangeReason(filterRanges?.ctor, CREATOR_UNAVAILABLE_REASONS.ctor)}
              >
                <PerformanceSimpleRange
                  form={form}
                  label="CTOR"
                  tooltip="Click-to-Order Rate"
                  description="Percentage of clicks that result in orders."
                  minField="minCtor"
                  maxField="maxCtor"
                  min={filterRanges?.ctor?.min ?? 0}
                  max={filterRanges?.ctor?.max || 100}
                  format="percent"
                />
              </UnavailableRange>
              <UnavailableRange
                form={form}
                fields={["minOrders", "maxOrders"]}
                reason={emptyRangeReason(filterRanges?.orders, CREATOR_UNAVAILABLE_REASONS.orders)}
              >
                <PerformanceSimpleRange
                  form={form}
                  label="Orders"
                  tooltip="Total Orders"
                  description="Total number of orders generated by the creator."
                  minField="minOrders"
                  maxField="maxOrders"
                  min={filterRanges?.orders?.min ?? 0}
                  max={filterRanges?.orders?.max || 100}
                  format="number"
                />
              </UnavailableRange>
              <UnavailableRange
                form={form}
                fields={["minAov", "maxAov"]}
                reason={emptyRangeReason(filterRanges?.aov, CREATOR_UNAVAILABLE_REASONS.aov)}
              >
                <PerformanceSimpleRange
                  form={form}
                  label="AOV"
                  tooltip="Average Order Value"
                  description="Average revenue per order."
                  minField="minAov"
                  maxField="maxAov"
                  min={filterRanges?.aov?.min ?? 0}
                  max={filterRanges?.aov?.max || 1000}
                  format="currency"
                />
              </UnavailableRange>
            </FilterSection>
          )}

          {/* Safety Risks Section */}
          {isAssurance && isActive("videoRiskStatus") && (
            <FilterSection id="safety-risks" icon={<ShieldZap className="w-5 h-5" />} title="Safety Risks">
              <FormField
                control={form.control}
                name="videoRiskStatus"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <LabelWithTooltip
                      label="Video Risk Status"
                      tooltip="Video Risk Status"
                      description="Filter creators based on whether their videos contain identified risks."
                    />
                    <HorizontalTabs
                      tabs={[
                        { id: "__all", label: "All" },
                        {
                          id: "no_risk",
                          label: "No Risks",
                          ...tabUnavailability(
                            unavailableUnless(
                              availability?.videoRiskStatus,
                              CREATOR_UNAVAILABLE_REASONS.noRisk,
                            )?.("no_risk"),
                            field.value === "no_risk",
                          ),
                        },
                        {
                          id: "has_risk",
                          label: "Has Risks",
                          ...tabUnavailability(
                            unavailableUnless(
                              availability?.videoRiskStatus,
                              CREATOR_UNAVAILABLE_REASONS.hasRisk,
                            )?.("has_risk"),
                            field.value === "has_risk",
                          ),
                        },
                      ]}
                      activeTab={field.value ?? "__all"}
                      onTabChange={(tabId) => field.onChange(tabId === "__all" ? undefined : tabId)}
                    />
                  </div>
                )}
              />

              {isActive("minVideosAtRiskPercent") && isActive("maxVideosAtRiskPercent") && (
                <UnavailableRange
                  form={form}
                  fields={["minVideosAtRiskPercent", "maxVideosAtRiskPercent"]}
                  reason={emptyRangeReason(filterRanges?.videosAtRiskPercent, CREATOR_UNAVAILABLE_REASONS.videosAtRisk)}
                >
                  <VideosAtRiskPercentFilter form={form} rangeData={filterRanges?.videosAtRiskPercent} />
                </UnavailableRange>
              )}

              {isActive("riskTier") && (
                <FormField
                  control={form.control}
                  name="riskTier"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <FormLabel className="text-sm text-tertiary">Risk Severity</FormLabel>
                      <PillField
                        value={field.value}
                        options={["1", "2", "3", "4"]}
                        onChange={field.onChange}
                        getLabel={(val) => RISK_TIER_LABELS[val] ?? `T${val}`}
                        getUnavailableReason={unavailable.riskTier}
                      />
                    </FormItem>
                  )}
                />
              )}

              {isActive("riskId") && (
                <FormField
                  control={form.control}
                  name="riskId"
                  render={({ field }) => <RiskTypeFilter field={field} getUnavailableReason={unavailable.riskId} />}
                />
              )}
            </FilterSection>
          )}

          {/* Creator Overview Section */}
          {isActive("creatorType") && (
            <FilterSection id="creator-overview" icon={<Users01 className="w-5 h-5" />} title="Creator Overview">
              <FormField
                control={form.control}
                name="creatorType"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <LabelWithTooltip
                      label="Creator Type"
                      tooltip="Creator Type"
                      description="All – every creator that has posted about your products. Creators – independent creators promoting your products. Brand – TikTok accounts owned by your brand."
                    />
                    <HorizontalTabs
                      tabs={[
                        { id: "all", label: "All", disabled: brandDisabled, disabledTooltip: BRAND_INACTIVE_TIP },
                        { id: "affiliate", label: "Creators" },
                        {
                          id: "brand",
                          label: "Merchant",
                          disabled: brandDisabled,
                          disabledTooltip: BRAND_INACTIVE_TIP,
                        },
                      ]}
                      activeTab={field.value ?? (brandDisabled ? "affiliate" : "all")}
                      onTabChange={(tabId) => field.onChange(tabId === "all" ? undefined : tabId)}
                    />
                  </div>
                )}
              />

              {isActive("minFollowers") && isActive("maxFollowers") && filterRanges && (
                <UnavailableRange
                  form={form}
                  fields={["minFollowers", "maxFollowers"]}
                  reason={zeroMaxReason(filterRanges.maxFollowers, CREATOR_UNAVAILABLE_REASONS.followers)}
                >
                  <FollowersRangeFilter form={form} max={filterRanges.maxFollowers || undefined} />
                </UnavailableRange>
              )}

              {isActive("topSellingCategories") && (
                <FormField
                  control={form.control}
                  name="topSellingCategories"
                  render={({ field }) => (
                    <TopSellingCategoriesFilter field={field} options={toOptions(filterRanges?.categories)} />
                  )}
                />
              )}

              {isActive("creatorLevel") && (advancedFilters?.creatorLevel?.length ?? 0) > 0 && (
                <FormField
                  control={form.control}
                  name="creatorLevel"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <FormLabel className="text-sm text-tertiary">Creator Level</FormLabel>
                      <PillField
                        value={field.value}
                        options={advancedFilters?.creatorLevel ?? []}
                        onChange={field.onChange}
                        getUnavailableReason={unavailable.creatorLevel}
                      />
                    </FormItem>
                  )}
                />
              )}

              {isActive("categoryPro") && (advancedFilters?.categoryPro?.length ?? 0) > 0 && (
                <FormField
                  control={form.control}
                  name="categoryPro"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <LabelWithTooltip
                        label="Category Pro"
                        tooltip="Category Pro"
                        description="Creators who regularly post about one product category and have generated most of their sales in that category in the past 60 days."
                      />
                      <PillField
                        value={field.value}
                        options={advancedFilters?.categoryPro ?? []}
                        onChange={field.onChange}
                        getUnavailableReason={unavailable.categoryPro}
                      />
                    </FormItem>
                  )}
                />
              )}

              {isActive("creatorLanguage") && (advancedFilters?.creatorLanguage?.length ?? 0) > 0 && (
                <FormField
                  control={form.control}
                  name="creatorLanguage"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <FormLabel className="text-sm text-tertiary">Creator Language</FormLabel>
                      <PillField
                        value={field.value}
                        options={advancedFilters?.creatorLanguage ?? []}
                        onChange={field.onChange}
                        getUnavailableReason={unavailable.creatorLanguage}
                      />
                    </FormItem>
                  )}
                />
              )}

              {isActive("minTotalLikes") && filterRanges && (
                <UnavailableRange
                  form={form}
                  fields={["minTotalLikes", "maxTotalLikes"]}
                  reason={zeroMaxReason(filterRanges.maxTotalLikes, CREATOR_UNAVAILABLE_REASONS.totalLikes)}
                >
                  <PerformanceSimpleRange
                    form={form}
                    label="Total Likes"
                    tooltip="Total Likes"
                    description="Cumulative likes across all creator content."
                    minField="minTotalLikes"
                    maxField="maxTotalLikes"
                    min={0}
                    max={filterRanges.maxTotalLikes || 1}
                    format="followers"
                  />
                </UnavailableRange>
              )}

              {isActive("minPostingFrequency") && filterRanges && (
                <UnavailableRange
                  form={form}
                  fields={["minPostingFrequency", "maxPostingFrequency"]}
                  reason={zeroMaxReason(filterRanges.maxPostingFrequency, CREATOR_UNAVAILABLE_REASONS.postingFrequency)}
                >
                  <PerformanceSimpleRange
                    form={form}
                    label="Posting Frequency"
                    tooltip="Posting Frequency"
                    description="Average number of videos posted per month."
                    minField="minPostingFrequency"
                    maxField="maxPostingFrequency"
                    min={0}
                    max={filterRanges.maxPostingFrequency || 1}
                    format="frequency"
                  />
                </UnavailableRange>
              )}
            </FilterSection>
          )}

          {/* Creator Attributes Section */}
          {isActive("toneAndApproach") && (
            <FilterSection id="creator-attributes" icon={<Palette className="w-5 h-5" />} title="Creator Attributes">
              <FormField
                control={form.control}
                name="toneAndApproach"
                render={({ field }) => (
                  <ToneAndApproachFilter field={field} options={toTitleCaseOptions(filterRanges?.toneAndApproach)} />
                )}
              />

              {isActive("contentFocus") && (
                <FormField
                  control={form.control}
                  name="contentFocus"
                  render={({ field }) => (
                    <ContentFocusFilter field={field} options={toOptions(filterRanges?.contentFocus)} />
                  )}
                />
              )}

              {isActive("productionQuality") && (
                <FormField
                  control={form.control}
                  name="productionQuality"
                  render={({ field }) => (
                    <ProductionQualityFilter field={field} options={toOptions(filterRanges?.productionQuality)} />
                  )}
                />
              )}
            </FilterSection>
          )}

          {/* Audience Section */}
          {(isActive("maleAudiencePercent") || isActive("gender") || isActive("age")) && (
            <FilterSection id="audience" icon={<Globe05 className="w-5 h-5" />} title="Audience">
              {isActive("maleAudiencePercent") && (
                <FormField
                  control={form.control}
                  name="maleAudiencePercent"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <Label className="text-sm text-tertiary">Male Audience</Label>
                      <PercentileSelector
                        value={(field.value as PercentileValue) ?? "all"}
                        onChange={(val) => field.onChange(val === "all" ? undefined : val)}
                        getUnavailableReason={unavailable.maleAudiencePercent}
                      />
                    </div>
                  )}
                />
              )}

              {isActive("femaleAudiencePercent") && (
                <FormField
                  control={form.control}
                  name="femaleAudiencePercent"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <Label className="text-sm text-tertiary">Female Audience</Label>
                      <PercentileSelector
                        value={(field.value as PercentileValue) ?? "all"}
                        onChange={(val) => field.onChange(val === "all" ? undefined : val)}
                        getUnavailableReason={unavailable.femaleAudiencePercent}
                      />
                    </div>
                  )}
                />
              )}

              {isActive("age") && (
                <FormField
                  control={form.control}
                  name="age"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <LabelWithTooltip
                        label="Audience Age Range"
                        tooltip="Audience Age Range"
                        description="Age group making up >30% of audience."
                      />
                      <PillField
                        value={field.value}
                        options={["18-24", "25-34", "35-44", "45-54", "55+"]}
                        onChange={field.onChange}
                        getUnavailableReason={unavailable.age}
                      />
                    </div>
                  )}
                />
              )}
            </FilterSection>
          )}

          {/* Featured Products Section */}
          {isActive("product") && (
            <FilterSection
              id="featured-products"
              icon={<ShoppingBag01 className="w-5 h-5" />}
              title="Featured Products"
            >
              <FormField
                control={form.control}
                name="product"
                render={({ field }) => <ProductFilter field={field} />}
              />
            </FilterSection>
          )}

          <TagsFilterSection />

          {/* Cohorts Section */}
          {isActive("includeCohorts") && (
            <FilterSection id="cohorts" icon={<Users01 className="w-5 h-5" />} title="Cohorts">
              <div data-testid="filter-cohort-include">
                <FormField
                  control={form.control}
                  name="includeCohorts"
                  render={({ field }) => <CohortFilter field={field} label="Include cohorts" disableUnassigned />}
                />
              </div>
              <div data-testid="filter-cohort-exclude">
                <FormField
                  control={form.control}
                  name="excludeCohorts"
                  render={({ field }) => <CohortFilter field={field} label="Exclude cohorts" />}
                />
              </div>
            </FilterSection>
          )}

          {/* Lists Section */}
          {isActive("creatorList") && (
            <FilterSection id="lists" icon={<Clipboard className="w-5 h-5" />} title="Lists">
              <FormField
                control={form.control}
                name="creatorList"
                render={({ field }) => (
                  <CreatorListFilter field={field} availableClassifications={availability?.creatorClassifications} />
                )}
              />
            </FilterSection>
          )}

          {/* Ad Campaigns Section */}
          {isActive("campaign") && (
            <FilterSection id="ad-campaigns" icon={<Announcement03 className="w-5 h-5" />} title="Ad Campaigns">
              <FormField
                control={form.control}
                name="campaign"
                render={({ field }) => <CampaignFilter field={field} />}
              />
            </FilterSection>
          )}
        </Accordion>
      </div>
    </Form>
  );
};
